import { useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  getMembershipsByMembershipId,
  getPackagesByPackageId,
  getUserOrdersByShopDisplayOrderId,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { Icon } from "atoms/icon/Icon";
import Affirm from "atoms/icon/icons/affirm.svg";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Klarna from "atoms/icon/icons/klarna.svg";
import RepeatMD from "atoms/icon/icons/RepeatCash.svg";
import Sunbit from "atoms/icon/icons/sunbit.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { getGiftCardTransfersByOrderId } from "modules/giftCards/giftCards.utils";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";

const load = async (shopDisplayOrderId: string) => {
  const orderHistoryDetails = queryClient.fetchQuery({
    ...getUserOrdersByShopDisplayOrderId.keyed({ shopDisplayOrderId }),
  });

  const giftCardTransfersPromise = queryClient.fetchQuery({
    ...getGiftCardTransfersByOrderId.keyed({ orderId: shopDisplayOrderId }),
  });

  return waitObj({
    orderHistoryDetails: orderHistoryDetails,
    initialGiftCardTransfers: giftCardTransfersPromise,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const orderHistoryDetailsLoader: LoaderFunction = async ({
  params: { shopDisplayOrderId },
}) => {
  if (!shopDisplayOrderId) return redirect("..");
  return await load(shopDisplayOrderId);
};

export const orderHistoryDetailsAction: ActionFunction = async () => {
  return true;
};

const financingNames = ["affirm", "klarna", "sunbit"] as const;
const isFinancingName = (name: unknown): name is (typeof financingNames)[number] =>
  financingNames.includes(name as any);

const FinancedPaymentMethod = (props: { name: (typeof financingNames)[number] }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 border-t-[1px] pt-3">
      <div className="pt-3 text-bold2">{t("orderHistory.paymentMethod")}</div>
      <div>
        <Icon svg={{ affirm: Affirm, klarna: Klarna, sunbit: Sunbit }[props.name]} />
      </div>
      <div className="-mt-2 text-body3 text-secondary">
        {t("orderHistory.financedThrough", {
          provider: { affirm: "Affirm", klarna: "Klarna", sunbit: "Sunbit" }[props.name],
        })}
      </div>
    </div>
  );
};

export const OrderHistoryDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shopDisplayOrderId } = useParams();
  const { orderHistoryDetails, initialGiftCardTransfers } = useLoaderData() as LoaderData;
  const isPaidWithRepeatCash =
    orderHistoryDetails.orderSummary.estimatedTotal === 0 &&
    !!orderHistoryDetails.orderSummary.repeatCashApplied;
  const isUnitedDerm = useFeatureFlag("unitedDerm");

  const memberships = useQueries({
    queries: orderHistoryDetails.membershipLineItems.map((lineItem) => {
      return getMembershipsByMembershipId.keyed({
        membershipId: lineItem.membershipId,
      });
    }),
  });

  const packages = useQueries({
    queries: orderHistoryDetails.packageLineItems.map((lineItem) => {
      return getPackagesByPackageId.keyed({
        packageId: lineItem.packageId,
      });
    }),
  });

  // Use initial data from loader to prevent race condition where gift cards
  // show in both packageLineItems and giftCardTransfers during loading
  const { data: giftCardTransfers = initialGiftCardTransfers ?? [] } = useQuery({
    queryKey: getGiftCardTransfersByOrderId.key({
      orderId: shopDisplayOrderId ?? "",
    }),
    queryFn: () =>
      getGiftCardTransfersByOrderId({
        orderId: shopDisplayOrderId ?? "",
      }),
    enabled: Boolean(shopDisplayOrderId),
    initialData: initialGiftCardTransfers,
  });

  // Get all package IDs that are gift cards for filtering
  const giftCardPackageIds = new Set(
    giftCardTransfers.map((transfer) => String(transfer.giftCardItemId)),
  );

  const renderGiftCardName = (transfer: {
    senderPlatformUserId: string;
    recipientPlatformUserId: string;
    recipientInformation: string;
    giftCardItemAmountInCents: number;
  }): string => {
    const isSelfPurchase = transfer.senderPlatformUserId === transfer.recipientPlatformUserId;

    if (isSelfPurchase) {
      return t("orderHistory.giftCardForYou", {
        amount: formatCurrency(transfer.giftCardItemAmountInCents),
      });
    }

    const recipient = transfer.recipientInformation.split(" ")[0];
    if (recipient) {
      return t("orderHistory.giftCardTo", {
        recipient,
        amount: formatCurrency(transfer.giftCardItemAmountInCents),
      });
    }
    return t("orderHistory.giftCard");
  };

  const nonFinancedPaymentName = orderHistoryDetails.paymentMethod?.nonFinancedPayment?.name;

  const refundedDateLabel = orderHistoryDetails.refundDetails?.refundedOn
    ? dayjs(orderHistoryDetails.refundDetails.refundedOn).format("MMM D, YYYY")
    : "";

  return (
    <div className="relative h-full bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">{t("orderHistory.header")}</div>
      </TopNav>
      <div className="p-5">
        <div className="flex flex-col gap-5 rounded-lg bg-white px-7 py-6 shadow-subtle">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <div className="text-bold1 capitalize text-primary">
                {t("orderHistory.orderNumber", { number: orderHistoryDetails.orderNumber })}
              </div>
              <div className="text-body1">{dayjs(orderHistoryDetails.date).format("L")}</div>
            </div>
            {orderHistoryDetails.patientName && (
              <div className="flex justify-between">
                <div className="text-bold1 capitalize text-primary">
                  {t("orderHistory.clientName")}
                </div>
                <div className="text-body1">{orderHistoryDetails.patientName}</div>
              </div>
            )}
            {orderHistoryDetails.platformLocationName && (
              <div className="flex justify-between">
                <div className="text-bold1 capitalize text-primary">
                  {t("orderHistory.purchaseLocation")}
                </div>
                <div className="text-body1">{orderHistoryDetails.platformLocationName}</div>
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <div className="flex flex-1 justify-start text-bold3">{t("orderHistory.product")}</div>
            <div className="flex flex-1 justify-center text-bold3">{t("orderHistory.qty")}</div>
            <div className="flex flex-1 justify-end text-bold3">{t("orderHistory.price")}</div>
          </div>
          <div className="flex flex-col gap-4 border-y-[1px] border-light-grey py-4">
            {orderHistoryDetails.packageLineItems
              .filter((lineItem) => {
                return !giftCardPackageIds.has(String(lineItem.packageId));
              })
              .map((lineItem, index) => {
                const packageModel = packages.find(
                  (packageModel) => packageModel.data?.id === lineItem.packageId,
                );
                if (!packageModel) return null;
                return (
                  <div
                    key={`package-${lineItem.packageId}-${index}`}
                    className="flex justify-between"
                  >
                    <div className="flex flex-1 justify-start text-body2">
                      {lineItem.displayName && lineItem.displayName != packageModel.data?.name
                        ? `${lineItem.displayName} - ${packageModel.data?.name}`
                        : packageModel.data?.name}
                    </div>
                    <div className="flex flex-1 justify-center text-body2">
                      {lineItem.quantity || 1}
                    </div>
                    <div className="flex flex-1 justify-end text-body2">
                      {formatCurrency(lineItem.cost)}
                    </div>
                  </div>
                );
              })}
            {orderHistoryDetails.membershipLineItems.map((lineItem) => {
              const membership = memberships.find(
                (membership) => membership.data?.id === lineItem.membershipId,
              );
              if (!membership) return null;
              return (
                <div key={lineItem.membershipId} className="flex justify-between">
                  <div className="flex flex-1 justify-start text-body2">
                    {lineItem.displayName && lineItem.displayName != membership.data?.name
                      ? `${lineItem.displayName} - ${membership.data?.name}`
                      : membership.data?.name}
                  </div>
                  <div className="flex flex-1 justify-center text-body2">1</div>
                  <div className="flex flex-1 justify-end text-body2">
                    {formatCurrency(lineItem.cost)}
                  </div>
                </div>
              );
            })}
            {giftCardTransfers.map((transfer) => {
              const giftCardName = renderGiftCardName(transfer);
              return (
                <div key={transfer.id} className="flex justify-between">
                  <div className="flex flex-1 justify-start text-body2">{giftCardName}</div>
                  <div className="flex flex-1 justify-center text-body2">1</div>
                  <div className="flex flex-1 justify-end text-body2">
                    {formatCurrency(transfer.giftCardItemAmountInCents)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between">
              <div className="text-body2">{t("common.subtotal")}</div>
              <div className="text-body2">
                {formatCurrency(orderHistoryDetails.orderSummary.subTotal)}
              </div>
            </div>
            {!!orderHistoryDetails.orderSummary.promoCodeDiscount && (
              <div className="flex justify-between">
                <div className="text-body2">
                  {t("orderHistory.discount", { code: orderHistoryDetails.promoCode })}
                </div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.promoCodeDiscount)}
                </div>
              </div>
            )}
            {!!orderHistoryDetails.orderSummary.membershipDiscount && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.membershipDiscount")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.membershipDiscount)}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.repeatCashApplied != 0 && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.repeatCashBalance")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.repeatCashApplied)}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.serviceFee > 0 && (
              <div className="flex justify-between">
                <div className="text-body2">
                  {!isUnitedDerm
                    ? t("orderHistory.convenienceFee")
                    : t("orderHistory.creditCardFee")}
                </div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.serviceFee)}
                </div>
              </div>
            )}
            {!!orderHistoryDetails.orderSummary.setupFee && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.enrollmentFee")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.setupFee)}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.salesTax > 0 && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.tax")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.salesTax ?? 0)}
                </div>
              </div>
            )}
            {!!orderHistoryDetails.orderSummary.tip && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.tip")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.tip)}
                </div>
              </div>
            )}
            {orderHistoryDetails.orderSummary.shippingFee != null && (
              <div className="flex justify-between">
                <div className="text-body2">{t("orderHistory.shipping")}</div>
                <div className="text-body2">
                  {formatCurrency(orderHistoryDetails.orderSummary.shippingFee)}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <div className="text-bold2">{t("orderHistory.orderTotal")}</div>
              <div className="text-bold2">
                {formatCurrency(orderHistoryDetails.orderSummary.estimatedTotal)}
              </div>
            </div>
            {orderHistoryDetails.refundDetails && (
              <>
                <div className="flex justify-between">
                  <div className="text-body2">
                    {orderHistoryDetails.refundDetails.isPartialRefund
                      ? t("orderHistory.totalRefunded")
                      : t("orderHistory.refundedOn", { date: refundedDateLabel })}
                  </div>
                  <div className="text-body2">
                    (
                    {formatCurrency(
                      orderHistoryDetails.refundDetails.refundedAmount ??
                        (isPaidWithRepeatCash
                          ? orderHistoryDetails.orderSummary.repeatCashApplied
                          : orderHistoryDetails.orderSummary.estimatedTotal),
                    )}
                    )
                  </div>
                </div>
                {orderHistoryDetails.refundDetails.isPartialRefund && (
                  <div className="flex justify-between">
                    <div className="text-bold2">{t("orderHistory.orderTotalAfterRefund")}</div>
                    <div className="text-bold2">
                      {formatCurrency(
                        orderHistoryDetails.orderSummary.estimatedTotal -
                          (orderHistoryDetails.refundDetails.refundedAmount ?? 0),
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {isFinancingName(nonFinancedPaymentName) && (
            <FinancedPaymentMethod name={nonFinancedPaymentName} />
          )}
          {isPaidWithRepeatCash && (
            <div className="flex flex-col gap-2 border-t-[1px] pt-3">
              <div className="pt-3 text-bold2">{t("orderHistory.paymentMethod")}</div>
              <div className="flex items-center gap-1">
                <Icon svg={RepeatMD} />
                <div className="text-bold2">RepeatCash</div>
              </div>
              <div className="text-body3 text-secondary">
                {t("orderHistory.repeatCashCompleted")}
              </div>
            </div>
          )}
          {orderHistoryDetails.paymentMethod?.financedPayment?.last4 && (
            <div className="flex flex-col gap-2 border-t-[1px] pt-3">
              <div className="pt-3 text-bold2">{t("orderHistory.paymentMethod")}</div>
              <div className="text-body2">
                <span className="capitalize">
                  {orderHistoryDetails.paymentMethod?.financedPayment?.cardType}{" "}
                </span>
                <span>
                  {t("orderHistory.endingIn", {
                    last4: orderHistoryDetails.paymentMethod?.financedPayment?.last4,
                  })}
                </span>
              </div>
              <div className="text-body3 text-secondary">{t("orderHistory.billingStatement")}</div>
            </div>
          )}
          {orderHistoryDetails.isFullDisputeWon && (
            <InfoBox
              // even tho' it's a success info box, we left it to type 'error' because the style of the error type matches the design from lovable (to explore if should create a new style for this)
              type="error"
              text={t("orderHistory.disputeWon")}
            />
          )}
        </div>
      </div>
      <div className="relative inset-x-0 bottom-0 mx-auto p-4 pb-9 desk:w-full sm:w-[390px]">
        <RepeatMDFooter />
      </div>
    </div>
  );
};
